import { mdiMagnify, mdiPlus } from '@mdi/js';
import { toRaw } from 'vue';
import UserRole from '@/constants/userrole';

const HonorificTitleTypes = {
  NotNeeded: { title: '不要/込み', value: 'not_needed' },
  Sama: { title: '様', value: 'sama' },
  Onchu: { title: '御中', value: 'onchu' },
};
const HonorificTitleTypeList = Object.values(HonorificTitleTypes);
const HonorificTitleTypeByValue = Object.fromEntries(
  HonorificTitleTypeList.map(x => [x.value, x])
);
const ErrMsgs = {
  Required: '必須です。',
  AddressKeisyo: '敬称が含まれています。',
  AddressLength: '最大7行、1行あたり35文字(70bytes)です。',
};

export default {
  props: {
    commit: {
      type: Function,
      default: () => true
    }
  },
  data: () => ({
    isOpened: false,
    isValid: true,
    values: {
      billing_id: null,
      language_id: 1,
      address_plan: '',
      use_inquiry_address: false,
      address_fulltext: '',
      honorific_title_type: HonorificTitleTypes.NotNeeded.value,
      role_id: null,
    },
    isPushed: false,
    HonorificTitleTypeList,
    mdiMagnify,
    mdiPlus,
  }),
  computed: {
    addressRules() {
      return this.values.use_inquiry_address ? [
        (v) => !!v || ErrMsgs.Required,
        (v) => this.validateAddressKeisyo(v) || ErrMsgs.AddressKeisyo,
        (v) => this.validateAddressLength(v) || ErrMsgs.AddressLength,
      ] : [];
    },
    isUncommittable() {
      return ![UserRole.ADMIN, UserRole.MANAGER].includes(this.values.role_id) || this.isPushed;
    },
  },
  watch: {
    'values.use_inquiry_address'() {
      this.validate();
    },
    'values.honorific_title_type'() {
      this.validate();
    },
  },
  methods: {
    open(values = {}) {
      Object.assign(this.values, this.$options.data.call(this).values, values);
      this.isOpened = true;
    },
    close() {
      this.isOpened = false;
    },
    toggleDetail() {
      const {
        honorific_title_type
      } = this.$options.data.call(this).values;
      this.values.address_fulltext = this.values.use_inquiry_address
        ? this.values.address_plan
        : '';
      this.values.honorific_title_type = honorific_title_type;
    },
    async validate() {
      return this.$refs.form?.validate();
    },
    async execSave() {
      if (this.isPushed) return;
      this.isPushed = true;
      try {
        if (!(await this.validate()).valid) return;
        const values = toRaw(this.values);
        await this.commit({
          billing_id: this.values.billing_id,
          use_inquiry_address: values.use_inquiry_address,
          address_fulltext: values.address_fulltext,
          honorific_title_type: values.honorific_title_type,
        });
        this.close();
      } finally {
        this.isPushed = false;
      }
    },

    /**
     * 文末に敬称が付いていないことを検証
     * ※請求書送付先の使用有無がONである前提
     * ※文頭文末は事前にトリムされている前提
     * @param {string} v 請求書送付先
     * @returns {bool}
     */
    validateAddressKeisyo(v) {
      // 空の場合は成功を返す
      if (!v) return true;

      // 敬称タイプ
      const htt = this.values.honorific_title_type;
      // 敬称タイプが「不要/込み」の場合は成功を返す
      if (htt === HonorificTitleTypes.NotNeeded.value) return true;

      const titles = Object.values(HonorificTitleTypes)
        .filter((v) => v !== HonorificTitleTypes.NotNeeded)
        .map((v) => v.title);

      // 文末に敬称が付かない場合は成功を返す
      return !(new RegExp(`(${titles.join('|')})$`)).test(v);
    },

    /**
     * 加工した請求書送付先が7行を超えないことを検証
     * ※加工⇒敬称を追加し最大文字数毎に折り返す
     * ※請求書送付先の使用有無がONである前提
     * ※文頭文末は事前にトリムされている前提
     * @param {string} v 請求書送付先
     * @returns {true|string}
     */
    validateAddressLength(v) {
      // 空の場合は成功を返す
      if (!v) return true;

      let text = toRaw(v);

      // 敬称タイプ
      const htt = this.values.honorific_title_type;
      // 敬称タイプが「不要/込み」以外の場合
      if (htt !== HonorificTitleTypes.NotNeeded.value) {
        // 選択された敬称を末尾に追加
        text = text.concat(HonorificTitleTypeByValue[htt].title);
      }

      // 言語ID (1:日本語、2:英語)
      const lng = this.values.language_id;
      // 言語IDにより最大文字数を決定 (日本語:35文字、英語:70文字)
      const mc = ([35, 70])[lng - 1];
      // 最大文字数毎にマッチ
      const re = new RegExp(`.{1,${mc}}`, 'g');
      // 改行で分割しつつ、各行内を最大文字数毎に分割
      const x = text
        .split(/(?:\r\n|\r|\n)/g)
        .flatMap((x) => x.match(re));

      // 7行以下の場合は成功を返す
      return x.length <= 7;
    },
  },
};
